import {
  EstimateFeeType,
  sessionEstimateItemTitles,
  TravelActivityTypes,
  WorkActivityTypes,
  WorkTimeType,
} from '@shared/types';
import { getWorkTimeType } from '@shared/utils';
import {
  Session,
  SessionActivity,
  SessionEstimate,
  SessionEstimateItem,
  SessionEstimateItemUnitType,
  TRoleId,
  TSessionActivityId,
} from '@talon/web/lib/api';
import dayjs from 'dayjs';

/**
 * A session estimate's items, shared out among the session's activities for the Session Estimator's per-activity
 * hours, wages and totals. The API prices a role's day as a whole, so this works back from its items.
 */
export const getSessionActivityItems = (session: Session, sessionEstimate: SessionEstimate) => {
  const timestep = 0.25;
  let processedBaseFee = false;

  const estimateItems: Record<TSessionActivityId, SessionEstimateItem[]> = {};
  const activitiesByRole: Record<TRoleId, SessionActivity[]> = {};

  session?.activities.forEach((activity) => {
    activity.roles.forEach((role) => {
      activitiesByRole[role.id] ??= [];
      activitiesByRole[role.id].push(activity);
    });
  });

  for (const roleId of Object.keys(activitiesByRole)) {
    const activities = activitiesByRole[roleId].sort((a, b) => dayjs(a.start_time ?? 0).diff(b.start_time ?? 0));

    const items = sessionEstimate?.items.filter((i) => i.role_id === roleId);
    const workTimeItems: Record<string, SessionEstimateItem | undefined> = {
      [WorkTimeType.StraightTime]: items?.find((i) =>
        i.title.toLocaleLowerCase().includes(sessionEstimateItemTitles[WorkTimeType.StraightTime].toLocaleLowerCase())
      ),
      [WorkTimeType.TimeAndOneHalf]: items?.find((i) =>
        i.title.toLocaleLowerCase().includes(sessionEstimateItemTitles[WorkTimeType.TimeAndOneHalf].toLocaleLowerCase())
      ),
      [WorkTimeType.DoubleTime]: items?.find((i) =>
        i.title.toLocaleLowerCase().includes(sessionEstimateItemTitles[WorkTimeType.DoubleTime].toLocaleLowerCase())
      ),
    };

    let totalHours = 0;

    for (const a of activities) {
      estimateItems[a.id] ??= [];

      if (!a.hours) continue;

      // Get work time
      if (WorkActivityTypes.has(a.type)) {
        const h = a.hours ?? 0;

        const estimates: Record<string, SessionEstimateItem | undefined> = {
          [WorkTimeType.StraightTime]: undefined,
          [WorkTimeType.TimeAndOneHalf]: undefined,
          [WorkTimeType.DoubleTime]: undefined,
        };

        for (let i = 0; i < h; i += timestep) {
          totalHours += timestep;
          const workTimeType = getWorkTimeType(totalHours);

          const workTimeItem = workTimeItems[workTimeType];
          if (workTimeItem) {
            let estimate = estimates[workTimeType];
            if (!estimate || typeof estimate.units !== 'number') {
              estimate = { ...workTimeItem, units: 0 };
              estimates[workTimeType] = estimate;
            }
            estimate.units = (estimate.units ?? 0) + timestep;

            const currentUnits = estimate.units ?? 0;
            const originalUnits = workTimeItem.units ?? 0;

            if (originalUnits) {
              const multiplier = currentUnits / originalUnits;
              estimate.subtotal = workTimeItem.subtotal * multiplier;
              estimate.subtotal_plus_fringe = workTimeItem.subtotal_plus_fringe * multiplier;
            }
          }
        }

        estimateItems[a.id].push(...Object.values(estimates).filter((e): e is SessionEstimateItem => !!e));
      } else {
        const matches =
          sessionEstimate?.items.filter(
            (item) =>
              item.unit_type === SessionEstimateItemUnitType.Hours &&
              item.role_id === roleId &&
              (item.session_activity?.type === a.type ||
                item.title.toLocaleLowerCase().includes(a.type.toLocaleLowerCase()))
          ) ?? [];

        const totalUnits =
          (!matches[1]
            ? matches[0]?.units
            : matches.map((item) => item.units ?? 0).reduce((acc, hours) => (acc ?? 0) + hours)) ?? 0;

        for (const match of matches) {
          const auditionEstimateItem: SessionEstimateItem = { ...match };

          const currentValue = a.hours ?? 0;
          const originalValue = totalUnits ?? 0;

          if (currentValue) {
            const multiplier = currentValue / originalValue;
            auditionEstimateItem.units = currentValue;
            auditionEstimateItem.subtotal *= multiplier;
            auditionEstimateItem.subtotal_plus_fringe *= multiplier;
          }

          estimateItems[a.id].push(auditionEstimateItem);
        }
      }

      // Look for any Base Session Fee estimate items and add them
      if (!processedBaseFee) {
        const baseFeeTitle = 'Base Session Fee'.toLocaleLowerCase();

        const matches =
          sessionEstimate?.items.filter(
            (item) => item.role_id === roleId && item.title.toLocaleLowerCase().includes(baseFeeTitle)
          ) ?? [];

        if (matches.length) estimateItems[a.id].push(...matches);
        processedBaseFee = true;
      }

      // If it's a travel day, check for No Service Day items
      if (TravelActivityTypes.has(a.type)) {
        const matches =
          sessionEstimate?.items.filter((item) => {
            if (item.role_id !== roleId) return false;
            const title = item.title.toLocaleLowerCase();

            if (title.includes('No Service Day'.toLocaleLowerCase())) {
              return true;
            } else if (title.includes(EstimateFeeType.StudioZoneTravelFee.toLocaleLowerCase())) {
              return true;
            } else {
              return false;
            }
          }) ?? [];

        if (matches.length) estimateItems[a.id].push(...matches);
      }

      // Add any associated premiums (by the hour: items by count are shared out below)
      const premiums =
        sessionEstimate?.items.filter(
          (item) =>
            item.role_id === roleId &&
            item.unit_type !== SessionEstimateItemUnitType.Count &&
            item.title.toLocaleLowerCase().includes((sessionEstimateItemTitles[a.type] ?? a.type).toLocaleLowerCase())
        ) ?? [];

      if (premiums.length) estimateItems[a.id].push(...premiums);
    }

    // Non-Activity fees: this role's items by count, shared out among its activities
    for (const item of sessionEstimate.items) {
      if (item.role_id === roleId && item.unit_type === SessionEstimateItemUnitType.Count) {
        let totalHours = 0;
        const itemActivities =
          activities.filter((activity) => {
            if (
              activity.roles?.find((role) => role.id === roleId) &&
              (activity.type === item.session_activity?.type ||
                item.title
                  .toLocaleLowerCase()
                  .includes((sessionEstimateItemTitles[activity.type] || activity.type)?.toLocaleLowerCase()))
            ) {
              totalHours += activity.hours ?? 0;
              return true;
            }
          }) ?? [];

        for (const activity of itemActivities) {
          const sessionEstimateItem: SessionEstimateItem = {
            ...item,
            session_activity: activity,
            session_activity_id: activity.id,
          };

          if (totalHours) {
            const multiplier = (activity.hours ?? 0) / totalHours;
            sessionEstimateItem.units = activity.hours ?? 0;
            sessionEstimateItem.subtotal *= multiplier;
            sessionEstimateItem.subtotal_plus_fringe *= multiplier;
          }

          estimateItems[activity.id].push(sessionEstimateItem);
        }
      }
    }
  }

  return estimateItems;
};
